import Swiper from "swiper";
import MainClass, { MAIN_CLASS_PARAMS } from "../utils/mainClass";
import { Mousewheel, Navigation, Pagination } from "swiper/modules";
import { _event } from "../utils/_event";
import { _lazyLoad } from "../utils/_lazyLoad";

class SliderCaseStudy extends MainClass {
    slides: number;

    el: {
        main: HTMLElement | null;
        wrapper: HTMLElement | null;
        slides: NodeListOf<HTMLElement>;
        indicator: HTMLElement | null;
    };

    instance: Swiper | null;
    currentSlide: number;

    constructor(props: MAIN_CLASS_PARAMS) {
        super(props);

        this.slides = this.selector.dataset.sliderSlides ? parseFloat(this.selector.dataset.sliderSlides) : 1;

        this.el = {
            main: this.selector.querySelector(".js-case-study-slider-main"),
            wrapper: this.selector.querySelector(".js-case-study-slider-wrapper"),
            slides: this.selector.querySelectorAll(".js-case-study-slider-item"),
            indicator: this.selector.querySelector(".js-case-study-slider-indicator"),
        };

        this.instance = null;
        this.currentSlide = 0;

        this.init();
    }

    init() {
        // for lazyload
        _lazyLoad();

        this.el.main?.classList.add("swiper");
        this.el.wrapper?.classList.add("swiper-wrapper");

        this.el.slides.forEach((item) => {
            item.classList.add("swiper-slide");
        });

        if (this.el.main)
            this.instance = new Swiper(this.el.main, {
                modules: [Mousewheel, Navigation, Pagination],
                loop: false,
                speed: 600,
                mousewheel: {
                    forceToAxis: true,
                    sensitivity: 1,
                },
                slidesPerView: this.slides,
                spaceBetween: 24,
                breakpoints: {
                    0: {
                        slidesPerView: this.slides === 1 ? 1 : "auto",
                        spaceBetween: 24,
                    },
                    640: {
                        slidesPerView: Math.min(2, this.slides),
                        spaceBetween: 24,
                    },
                    1024: {
                        slidesPerView: this.slides,
                        spaceBetween: 48,
                    },
                },
                on: {
                    init: (instance) => {
                        this.update(instance);
                    },
                    slideChange: () => {
                        this.update();
                    },
                },
            });

        // for slider btns
        this.selector.querySelectorAll(".js-case-study-slider-btn").forEach((item) => {
            const _item = item;

            _item.addEventListener("click", (e) => {
                e.preventDefault();

                // for prev
                if (_item.classList.contains("btn-prev")) {
                    if (this.instance) this.instance.slideTo(this.currentSlide - 1);
                }

                // for next
                if (_item.classList.contains("btn-next")) {
                    if (this.instance) this.instance.slideTo(this.currentSlide + 1);
                }
            });
        });

        // for indicator
        if (this.el.indicator) {
            const _line = this.el.indicator.querySelector(".js-case-study-slider-indicator-line");
            if (!_line) return;

            (_line.children[0] as HTMLElement).style.background =
                "linear-gradient(90deg, var(--theme-1,#415791) 0%, var(--theme-2,#8AB1B8) 80.08%, #E9E6E2 102.96%, #F6F7F7 114.4%)";
        }

        _event.resize((event, status) => {
            if (status === "resize") return;

            this.resize();
            this.update();
        });

        // console.log("SliderCaseStudy", this);
    }

    update(instance?: Swiper) {
        // get instance
        const _instance = instance ?? this.instance;
        if (!_instance) return;

        // get active slide
        this.currentSlide = _instance.activeIndex ?? 0;

        const _totalSlides = _instance.slides.length;
        const _slidesPerView = parseFloat(_instance.params.slidesPerView) ? _instance.params.slidesPerView : 2;
        const _currentPage = _instance.activeIndex / _slidesPerView;
        const _totalPages = _totalSlides / _slidesPerView;
        const _percent = ((_currentPage + 1) / _totalPages) * 100;

        // update indicator's width
        this.el.indicator?.style.setProperty("--width", `${_percent}%`);

        // update the btns
        this.selector.querySelectorAll(".js-case-study-slider-btn").forEach((item) => {
            const _item = item;

            if (_item.classList.contains("btn-prev")) {
                if (_currentPage === 0) {
                    _item.setAttribute("disabled", "");
                } else {
                    _item.removeAttribute("disabled");
                }
            }
            if (_item.classList.contains("btn-next")) {
                if (_currentPage.toFixed(2) === (_totalPages - 1).toFixed(2)) {
                    _item.setAttribute("disabled", "");
                } else {
                    _item.removeAttribute("disabled");
                }
            }
        });
    }

    resize() {
        const _section =
            this.selector.closest(".section-wrapper") ||
            this.selector.closest(".section") ||
            this.selector.closest("section") ||
            this.selector.closest(".section-container")?.parentNode;
        if (_section) {
            if (_event.width() >= 640) {
                _section.classList.remove("overflow-hidden");
                this.el.main?.classList.remove("overflow-visible");
                return;
            }

            const _style = getComputedStyle(_section);
            if (_style.position === "static") _section.classList.add("relative");
            _section.classList.add("overflow-hidden");

            this.el.main?.classList.add("overflow-visible");
        }
    }
}

export default SliderCaseStudy;
